if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', class CartCrossSell extends HTMLElement {
    static get observedAttributes() {
      return ['data-product-id', 'data-cart-ids'];
    }

    connectedCallback() {
      this._grid = this.querySelector('.magon-cart-xsell__grid');
      this._productId = this.dataset.productId;
      this._cartIds = (this.dataset.cartIds || '').split(',').map(Number).filter(Boolean);
      this._maxItems = parseInt(this.dataset.maxItems, 10) || 5;
      this._isStatic = this.dataset.static === 'true';
      this._fetching = false;

      if (this._isStatic) {
        this._attachHandlers();
      } else if (this._productId) {
        this._fetchRecommendations();
        this._observeGrid();
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (!this.isConnected || oldVal === newVal) return;
      this._grid = this.querySelector('.magon-cart-xsell__grid');
      this._productId = this.dataset.productId;
      this._cartIds = (this.dataset.cartIds || '').split(',').map(Number).filter(Boolean);
      this._maxItems = parseInt(this.dataset.maxItems, 10) || 5;
      if (!this._isStatic && this._productId) this._fetchRecommendations();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _observeGrid() {
      if (!this._grid || this._observer) return;
      this._observer = new MutationObserver(() => {
        if (
          this._grid &&
          this._grid.children.length === 0 &&
          this._productId &&
          !this._fetching
        ) {
          this._fetchRecommendations();
        }
      });
      this._observer.observe(this._grid, { childList: true });
    }

    _fetchRecommendations() {
      if (this._fetching) return;
      this._fetching = true;
      // Encadeia os intents: 'complementary' (se configurado no Search & Discovery),
      // depois 'related' (automático da Shopify, sempre relevante ao produto). Só
      // cai no fallback de mais-vendidos se AMBOS vierem vazios — evita recomendar
      // itens sem relação (ex: feminino para um produto masculino no carrinho).
      this._tryIntent('complementary');
    }

    _tryIntent(intent) {
      fetch('/recommendations/products.json?product_id=' + this._productId + '&limit=10&intent=' + intent)
        .then(r => r.json())
        .then(data => {
          const products = (data.products || [])
            .filter(p => !this._cartIds.includes(p.id))
            .slice(0, this._maxItems);
          if (products.length > 0) {
            this._render(products);
            this._fetching = false;
            return;
          }
          if (intent === 'complementary') {
            this._tryIntent('related');
          } else {
            this._fetching = false;
            this._fetchFallback();
          }
        })
        .catch(() => {
          if (intent === 'complementary') {
            this._tryIntent('related');
          } else {
            this._fetching = false;
            this._fetchFallback();
          }
        });
    }

    _fetchFallback() {
      if (this._fetching) return;
      this._fetching = true;
      fetch('/collections/all/products.json?limit=10&sort_by=best-selling')
        .then(r => r.json())
        .then(data => {
          const products = (data.products || [])
            .filter(p => !this._cartIds.includes(p.id))
            .slice(0, this._maxItems);
          if (!products.length) { this.style.display = 'none'; this._fetching = false; return; }
          this._render(products);
          this._fetching = false;
        })
        .catch(() => { this.style.display = 'none'; this._fetching = false; });
    }

    _attachHandlers() {
      if (!this._grid) return;
      this._grid.querySelectorAll('select').forEach(sel => {
        sel.addEventListener('change', () => {
          const btn = sel.parentElement.querySelector('.magon-cart-xsell__add-btn');
          if (btn) btn.disabled = !sel.value;
        });
      });
      this._grid.querySelectorAll('.magon-cart-xsell__add-btn').forEach(btn => {
        btn.addEventListener('click', () => this._addToCart(btn));
      });
    }

    _render(products) {
      this._grid.innerHTML = products.map(p => this._buildCard(p)).join('');
      this._attachHandlers();
    }

    _buildCard(p) {
      const url = p.url || '/products/' + p.handle;
      const title = this._esc(p.title || '');
      return `<div class="magon-cart-xsell__card">
        <a href="${url}" class="magon-cart-xsell__img">${this._buildImage(p)}</a>
        <a href="${url}" class="magon-cart-xsell__name">${title}</a>
        <p class="magon-cart-xsell__price">${this._formatPrice(p)}</p>
        ${this._buildForm(p)}
      </div>`;
    }

    _buildImage(p) {
      let src = p.featured_image
        || (p.images && p.images[0] && (p.images[0].src || p.images[0]));
      if (!src) return '';
      src = String(src).replace(/^\/\//, 'https://');
      if (!src.includes('width=')) {
        src += (src.includes('?') ? '&' : '?') + 'width=300';
      }
      return `<img src="${src}" alt="${this._attr(p.title || '')}" loading="lazy">`;
    }

    _buildForm(p) {
      const hasVariants = p.variants && p.variants.length > 1;
      if (hasVariants) {
        const opts = p.variants.map(v =>
          `<option value="${v.id}"${!v.available ? ' disabled' : ''}>${this._esc(v.title)}${!v.available ? ' – Esg.' : ''}</option>`
        ).join('');
        return `<select class="magon-cart-xsell__select">
            <option value="" disabled selected>Tamanho</option>${opts}
          </select>
          <button type="button" class="magon-cart-xsell__add-btn" disabled>Adicionar</button>`;
      }
      const v = p.variants[0];
      const avail = v && v.available;
      return `<input type="hidden" value="${v ? v.id : ''}">
        <button type="button" class="magon-cart-xsell__add-btn"${!avail ? ' disabled' : ''}>
          ${avail ? 'Adicionar' : 'Esgotado'}
        </button>`;
    }

    async _addToCart(btn) {
      const card = btn.closest('.magon-cart-xsell__card');
      const sel = card.querySelector('select');
      const hid = card.querySelector('input[type="hidden"]');
      const id = sel ? sel.value : (hid ? hid.value : null);
      if (!id) return;

      btn.textContent = '...';
      btn.disabled = true;

      try {
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map(el => el.dataset.sectionId)
          .filter(Boolean);

        const body = { id: parseInt(id, 10), quantity: 1 };
        if (sectionIds.length) body.sections = sectionIds.join(',');

        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.description || 'Falha');

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: String(id),
            data: {
              source: 'magon-cross-sell',
              itemCount: 1,
              sections: data.sections,
            }
          }
        }));

        btn.textContent = 'Adicionado ✓';
        setTimeout(() => { btn.textContent = 'Adicionar'; btn.disabled = false; }, 1500);
      } catch {
        btn.textContent = 'Erro';
        setTimeout(() => { btn.textContent = 'Adicionar'; btn.disabled = false; }, 1500);
      }
    }

    _formatPrice(p) {
      const raw = p.price != null ? p.price : (p.variants && p.variants[0] && p.variants[0].price);
      if (raw == null) return 'R$ —';
      const num = typeof raw === 'string' ? parseFloat(raw) : raw / 100;
      if (isNaN(num)) return 'R$ —';
      return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    _esc(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    _attr(str) {
      return String(str).replace(/"/g, '&quot;');
    }
  });
}
